import { apiFetch, getApiBaseUrl, getAuthToken } from "@/lib/api";

export type BookingPackageProposalSummary = {
  id: string;
  title?: string | null;
  main_destination?: string | null;
  clients?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
} | null;

export type BookingPackageUserSummary = {
  id: string;
  full_name: string;
} | null;

export type BookingPackageListItem = {
  id: string;
  status: string;
  created_at: string;
  proposal: BookingPackageProposalSummary;
  sales_owner: BookingPackageUserSummary;
  operations_owner: BookingPackageUserSummary;
};

export type BookingPackageListResponse = {
  items: BookingPackageListItem[];
  total: number;
};

export type BookingServiceTicketFile = {
  id: string;
  file_name: string | null;
  uploaded_at: string | null;
  download_url: string | null;
};

export type BookingService = {
  id: string;
  service_type: string;
  service_name: string;
  service_date: string | null;
  service_time: string | null;
  service_description: string | null;
  ticket_type: string | null;
  quantity: number | null;
  unit_value: number | null;
  total_value: number | null;
  internal_notes: string | null;
  public_note: string | null;
  no_ticket_required: boolean;
  ticket_file_name: string | null;
  ticket_uploaded_at: string | null;
  status: string | null;
  reservation_status: string;
  service_data: any;
  ticket_download_url: string | null;
  ticket_files: BookingServiceTicketFile[] | null;
};

export type BookingPackageDetail = {
  id: string;
  proposal_id: string;
  sales_owner_id?: string | null;
  manager_id?: string | null;
  operations_owner_id?: string | null;
  status: string;
  notes?: string | null;
  guests?: string | null;
  hotel?: string | null;
  contact?: string | null;
  created_at: string;
  updated_at?: string | null;
  proposal?: {
    id: string;
    title?: string | null;
    main_destination?: string | null;
    pax_adults?: number | null;
    pax_children?: number | null;
    pax_infants?: number | null;
    status?: string | null;
    clients?: {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    } | null;
  } | null;
  sales_owner?: BookingPackageUserSummary;
  operations_owner?: BookingPackageUserSummary;
  services?: BookingService[];
};

export type BookingPackagePublic = {
  id: string;
  proposal_id: string;
  status: string;
  guests?: string | null;
  hotel?: string | null;
  contact?: string | null;
  created_at: string;
  proposal: BookingPackageDetail["proposal"];
  services: BookingService[];
};

export type DownloadedFile = {
  blob: Blob;
  fileName: string | null;
};

const parseFileNameFromContentDisposition = (value: string | null): string | null => {
  if (!value) return null;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const quotedMatch = value.match(/filename=\"([^\"]+)\"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
};

const handleDownloadError = async (response: Response): Promise<never> => {
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);
  if (response.status === 413) {
    throw new Error("Maximum file size is 10MB.");
  }
  if (typeof payload === "string" && payload.trim()) {
    throw new Error(payload);
  }
  throw new Error(payload?.message ?? "Download failed");
};

export const fetchBookingPackages = async (params: {
  page: number;
  size: number;
  search?: string;
  status?: string;
}): Promise<BookingPackageListResponse> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);
  return apiFetch<BookingPackageListResponse>(`/booking-packages?${query.toString()}`);
};

export const getBookingPackage = async (id: string): Promise<BookingPackageDetail> => {
  return apiFetch<BookingPackageDetail>(`/booking-packages/${id}`);
};

export const getPublicBookingPackage = async (id: string): Promise<BookingPackagePublic> => {
  return apiFetch<BookingPackagePublic>(`/booking-packages/public/${id}`);
};

export const updateBookingServiceStatus = async (id: string, reservationStatus: string): Promise<BookingService> => {
  return apiFetch<BookingService>(`/booking-services/${id}/reservation-status`, {
    method: "PUT",
    body: JSON.stringify({ reservation_status: reservationStatus }),
  });
};

export const updateBookingServiceNote = async (id: string, note: string | null): Promise<BookingService> => {
  return apiFetch<BookingService>(`/booking-services/${id}/note`, {
    method: "PUT",
    body: JSON.stringify({ public_note: note }),
  });
};

export const uploadBookingServiceTicket = async (id: string, files: File | File[]): Promise<BookingService> => {
  const fileList = Array.isArray(files) ? files : [files];
  const formData = new FormData();
  fileList.forEach((file) => formData.append("files", file));
  const baseUrl = getApiBaseUrl();
  const token = getAuthToken();
  const response = await fetch(`${baseUrl}/booking-services/${id}/ticket`, {
    method: "POST",
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);
    if (response.status === 413) {
      throw new Error("Maximum file size is 10MB.");
    }
    if (typeof payload === "string" && payload.trim()) {
      throw new Error(payload);
    }
    throw new Error(payload?.message ?? "Upload failed");
  }
  return response.json();
};

export const deleteBookingServiceTicketFile = async (id: string, fileId: string): Promise<BookingService> => {
  return apiFetch<BookingService>(`/booking-services/${id}/ticket/${fileId}`, {
    method: "DELETE",
  });
};

export const downloadBookingServiceTicketFile = async (id: string, fileId?: string): Promise<DownloadedFile> => {
  const baseUrl = getApiBaseUrl();
  const token = getAuthToken();
  const path = fileId
    ? `/booking-services/${id}/ticket/${fileId}/download`
    : `/booking-services/${id}/ticket/download`;
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    await handleDownloadError(response);
  }
  const blob = await response.blob();
  const fileName = parseFileNameFromContentDisposition(response.headers.get("content-disposition"));
  return { blob, fileName };
};

export const downloadPublicBookingServiceTicketFile = async (
  bookingPackageId: string,
  serviceId: string,
  fileId?: string
): Promise<DownloadedFile> => {
  const baseUrl = getApiBaseUrl();
  const path = fileId
    ? `/booking-packages/public/${bookingPackageId}/services/${serviceId}/ticket/${fileId}/download`
    : `/booking-packages/public/${bookingPackageId}/services/${serviceId}/ticket/download`;
  const response = await fetch(`${baseUrl}${path}`, { method: "GET" });
  if (!response.ok) {
    await handleDownloadError(response);
  }
  const blob = await response.blob();
  const fileName = parseFileNameFromContentDisposition(response.headers.get("content-disposition"));
  return { blob, fileName };
};

export const updateBookingPackageInfo = async (
  id: string,
  info: { guests: string | null; hotel: string | null; contact: string | null }
): Promise<BookingPackageDetail> => {
  return apiFetch<BookingPackageDetail>(`/booking-packages/${id}/info`, {
    method: "PUT",
    body: JSON.stringify(info),
  });
};

export const finalizeBookingPackage = async (id: string): Promise<BookingPackageDetail> => {
  return apiFetch<BookingPackageDetail>(`/booking-packages/${id}/finalize`, {
    method: "POST",
  });
};

export const deleteAllBookingPackages = async (): Promise<void> => {
  await apiFetch<void>("/booking-packages", { method: "DELETE" });
};
